/**
 * Centralized API helper module
 * Provides caching, retry, and consistent error handling for all API calls.
 */

const API_URL = process.env.REACT_APP_API_URL;

// ─── Cache helpers ───────────────────────────────────────────────────────────
const cache = new Map();
const CACHE_TTL = 60000; // 60 seconds default

const getCached = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data;
};

const setCache = (key, data) => {
  cache.set(key, { data, ts: Date.now() });
};

// ─── Core request helper ─────────────────────────────────────────────────────
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

/**
 * Generic GET request with caching and error handling.
 * @param {string} path - API path (e.g., '/api/logo')
 * @param {object} opts - { cacheKey?, noCache?, params? }
 */
async function apiGet(path, opts = {}) {
  const { cacheKey, noCache, params } = opts;

  if (cacheKey && !noCache) {
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
  }

  let url = `${API_URL}${path}`;
  if (params) {
    const sp = new URLSearchParams(params);
    url += `?${sp.toString()}`;
  }

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`API Error: ${res.status} ${res.statusText} for ${path}`);
  }
  const data = await res.json();

  if (cacheKey) setCache(cacheKey, data);
  return data;
}

/**
 * POST request helper with retry logic for rate-limited endpoints.
 */
async function apiPost(path, body, opts = {}) {
  const { retries = 0 } = opts;
  const url = `${API_URL}${path}`;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 2000 * attempt));

      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw { status: res.status, data };
      return { ok: true, data, status: res.status };
    } catch (err) {
      lastError = err;
      if (err?.status === 429) continue; // rate limit retry
      throw err;
    }
  }
  throw lastError;
}

// ─── Predefined API calls (cached) ───────────────────────────────────────────

export const fetchLogo = () => apiGet('/api/logo', { cacheKey: 'logo' });

export const fetchGeneral = () => apiGet('/api/general', { cacheKey: 'general' });

export const fetchSliders = () => apiGet('/api/sliders', { cacheKey: 'sliders' });

export const fetchSocialLinks = () => apiGet('/api/sociallink', { cacheKey: 'sociallink' });

export const fetchPaymentMethods = () => apiGet('/api/payment-methods', { cacheKey: 'paymentMethods' });

export const fetchPromotions = () => apiGet('/api/promotion', { cacheKey: 'promotions' });

export const fetchUserData = (remoteId) => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(null);
  return apiGet(`/api/user/show?remote_id=${encodeURIComponent(remoteId)}`, { noCache: true });
};

export const fetchTransactions = (remoteId) => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve([]);
  return apiGet(`/api/transaction/show?remote_id=${encodeURIComponent(remoteId)}`, { noCache: true });
};

export const fetchTurnovers = (remoteId) => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve({ turnover: [] });
  return apiGet(`/api/turnovers?remote_id=${encodeURIComponent(remoteId)}`, { noCache: true });
};

export const fetchMarketBlock = () => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve([]);
  return apiGet('/api/market/block', { noCache: true });
};

export const fetchBalance = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const result = await apiPost('/api/balance', {});
  return result.data;
};

// ─── Invalidate cache helpers ────────────────────────────────────────────────
export const invalidateCache = (key) => {
  if (key) cache.delete(key);
  else cache.clear();
};

export { API_URL, getAuthHeaders, apiGet, apiPost };
